// frontend/src/pages/Facilitation/FacilitationDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  FormText,
} from 'reactstrap';
import { ordersAPI, facilitatorsAPI } from '../../services/api';
import PDFViewerModal from '../../components/Modals/PDFViewerModal';
import { generateFacilitationPDF } from '../../utils/pdfGenerator';
import { parseDate } from '../../utils/dates';

// Parsea fechas "YYYY-MM-DD" sin desplazamiento de timezone
function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = String(dateStr).split('-');
  if (!year || !month || !day) return dateStr;
  return new Date(+year, +month - 1, +day).toLocaleDateString('es-MX');
}

// Definido fuera del componente para evitar re-mount en cada keystroke
function EditableText({ field, label, type = 'text', textarea = false, value, onChange }) {
  return (
    <FormGroup className="mb-2">
      <Label className="text-xs font-weight-bold text-dark mb-1">{label}</Label>
      {textarea ? (
        <Input
          type="textarea"
          rows={3}
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          bsSize="sm"
        />
      ) : (
        <Input
          type={type}
          value={value || ''}
          onChange={e => onChange(field, e.target.value)}
          bsSize="sm"
        />
      )}
    </FormGroup>
  );
}

function FacilitationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');

  // Modo edición
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [facilitatorsList, setFacilitatorsList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAll();
  }, [id]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [orderRes, facRes] = await Promise.all([
        ordersAPI.getById(id),
        facilitatorsAPI.getAll(),
      ]);
      setOrder(orderRes.data);
      setFacilitatorsList(facRes.data.results || facRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar la orden');
      navigate('/admin/facilitation');
    } finally {
      setLoading(false);
    }
  };

  const openPdfViewer = (pdfPath) => {
    const url = pdfPath.startsWith('http')
      ? pdfPath
      : `http://localhost:8000/media/${pdfPath}`;
    setSelectedPdfUrl(url);
    setPdfModalOpen(true);
  };

  const downloadPdf = (pdfPath, filename) => {
    const link = document.createElement('a');
    link.href = `http://localhost:8000/media/${pdfPath}`;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    if (!order || !consolidatedData) {
      alert('No hay datos disponibles para generar el PDF');
      return;
    }
    generateFacilitationPDF(order, consolidatedData);
  };

  const buildEditData = (ord) => {
    const inv = ord?.invoice_schedule_details || {};
    const quote = ord?.quote_details || {};
    const sd = quote?.items?.service_details || {};
    return {
      // Logística
      service_delivery_date: inv.service_delivery_date || '',
      scheduled_invoice_date: inv.scheduled_invoice_date || '',
      facilitator: inv.facilitator || '',
      location: inv.location || ord.location || '',
      // Taller
      workshop_name: inv.workshop_name || '',
      session_schedule: inv.session_schedule || '',
      program_required: inv.program_required ?? false,
      diplomas_required: inv.diplomas_required ?? false,
      entry_requirements: inv.entry_requirements || '',
      entry_contact_name: inv.entry_contact_name || '',
      entry_contact_phone: inv.entry_contact_phone || '',
      // Cliente y servicio
      client_need: inv.client_need || ord.client_need || '',
      description: inv.description || '',
      total_hours: inv.total_hours || '',
      credit_days: inv.credit_days || '',
      notes: inv.notes || '',
      // Detalles del servicio (desde Quote)
      category: quote.category || '',
      service_type: quote.service_type || '',
      num_sessions: sd.num_sessions || '',
      num_people: sd.num_people || '',
      num_groups: sd.num_groups || '',
      language: sd.language || '',
      city_state: sd.city_state || '',
    };
  };

  const enterEditMode = () => {
    setEditData(buildEditData(order));
    setEditMode(true);
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ordersAPI.updateFacilitation(order.id, {
        service_delivery_date: editData.service_delivery_date || null,
        scheduled_invoice_date: editData.scheduled_invoice_date || null,
        facilitator: editData.facilitator || null,
        location: editData.location,
        workshop_name: editData.workshop_name,
        session_schedule: editData.session_schedule,
        program_required: editData.program_required,
        diplomas_required: editData.diplomas_required,
        entry_requirements: editData.entry_requirements,
        entry_contact_name: editData.entry_contact_name,
        entry_contact_phone: editData.entry_contact_phone,
        description: editData.description,
        total_hours: editData.total_hours || null,
        credit_days: editData.credit_days || null,
        notes: editData.notes,
        client_need: editData.client_need,
        // Detalles del servicio (Quote)
        category: editData.category,
        service_type: editData.service_type,
        num_sessions: editData.num_sessions,
        num_people: editData.num_people,
        num_groups: editData.num_groups,
        language: editData.language,
        city_state: editData.city_state,
      });

      await loadAll();
      setEditMode(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Error al guardar los cambios. Verifica los datos e intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando información completa...</p>
          </div>
        </Container>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">Orden no encontrada</h4>
              <Button color="primary" className="btn-icon mt-3" onClick={() => navigate('/admin/facilitation')}>
                <span className="btn-inner--icon"><i className="ni ni-bold-left"></i></span>
                <span className="btn-inner--text">Volver a Facilitación</span>
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  // Consolidar datos
  const quote = order.quote_details;
  const invoiceSchedule = order.invoice_schedule_details;
  const quoteItems = quote?.items || {};
  const serviceDetails = quoteItems.service_details || {};
  const clientInfo = quoteItems.client_info || {};
  const paymentInfo = quoteItems.payment || {};

  const consolidatedData = {
    clientName: clientInfo.client_name || quote?.client_name || order.billing_name || 'N/A',
    legalName: invoiceSchedule?.billing_name || clientInfo.legal_name || order.billing_name || 'N/A',
    rfc: invoiceSchedule?.billing_rfc || order.billing_rfc || 'N/A',
    address: invoiceSchedule?.billing_address || order.billing_address || 'N/A',
    contactName: quote?.contact_name || clientInfo.contact_name || order.buyer_name || 'N/A',
    contactEmail: quote?.contact_email || clientInfo.contact_email || order.buyer_email || 'N/A',
    contactPhone: order.buyer_phone || 'N/A',
    sellerName: quote?.seller_name || order.seller_name || 'N/A',
    supplierId: order.supplier_id || 'N/A',
    category: quote?.category || serviceDetails.category || 'N/A',
    serviceType: quote?.service_type || serviceDetails.service_type || 'N/A',
    totalHours: invoiceSchedule?.total_hours || serviceDetails.num_hours || 'N/A',
    numSessions: serviceDetails.num_sessions || 'N/A',
    numPeople: serviceDetails.num_people || 'N/A',
    numGroups: serviceDetails.num_groups || 'N/A',
    language: serviceDetails.language || 'N/A',
    cityState: serviceDetails.city_state || 'N/A',
    includes: serviceDetails.includes || '',
    facilitator: invoiceSchedule?.facilitator_name || 'N/A',
    location: invoiceSchedule?.location || order.location || '',
    clientNeed: invoiceSchedule?.client_need || order.client_need || '',
    serviceDeliveryDate: invoiceSchedule?.service_delivery_date || null,
    description: invoiceSchedule?.description || '',
    paymentTerms: order.payment_terms || paymentInfo.payment_terms || 'N/A',
    creditDays: invoiceSchedule?.credit_days || paymentInfo.credit_days || quote?.credit_days || 0,
    orderDate: order.order_date || null,
    deliveryDate: order.delivery_date || null,
    scheduledInvoiceDate: invoiceSchedule?.scheduled_invoice_date || null,
    hourlyRate: invoiceSchedule?.hourly_rate || serviceDetails.price_per_hour || null,
    responsibleUser: invoiceSchedule?.responsible_user_name || 'N/A',
  };

  const isScheduled = !!consolidatedData.serviceDeliveryDate;

  // Helper para campo editable o texto

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100" style={{ zIndex: 0 }}></div>

      <Container fluid className="py-4" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header con botones */}
        <Row className="mb-3">
          <Col xs="12" md="6" className="d-flex align-items-center mb-2 mb-md-0">
            <Button
              color="white"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={() => navigate('/admin/facilitation')}
            >
              <span className="btn-inner--icon"><i className="ni ni-bold-left"></i></span>
              <span className="btn-inner--text ms-1">Volver a Facilitación</span>
            </Button>
          </Col>
          <Col xs="12" md="6" className="d-flex justify-content-md-end gap-2 flex-wrap">
            {!editMode ? (
              <>
                <Button
                  color={!isScheduled ? 'warning' : 'default'}
                  size="sm"
                  className="btn-icon shadow-sm"
                  onClick={enterEditMode}
                >
                  <span className="btn-inner--icon">
                    <i className={`ni ${!isScheduled ? 'ni-calendar-grid-58' : 'ni-settings'}`}></i>
                  </span>
                  <span className="btn-inner--text ms-1">
                    {!isScheduled ? 'Programar Servicio' : 'Editar Programación'}
                  </span>
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  className="btn-icon shadow-sm"
                  onClick={handleDownloadPDF}
                >
                  <span className="btn-inner--icon"><i className="ni ni-cloud-download-95"></i></span>
                  <span className="btn-inner--text ms-1">Descargar PDF</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="success"
                  size="sm"
                  className="btn-icon shadow-sm"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <span className="btn-inner--icon">
                    <i className={saving ? 'ni ni-settings' : 'ni ni-check-bold'}></i>
                  </span>
                  <span className="btn-inner--text ms-1">{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="btn-icon shadow-sm"
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                >
                  <span className="btn-inner--icon"><i className="ni ni-fat-remove"></i></span>
                  <span className="btn-inner--text ms-1">Cancelar</span>
                </Button>
              </>
            )}
          </Col>
        </Row>

        {!isScheduled && !editMode && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-warning d-flex align-items-center" role="alert">
                <i className="ni ni-time-alarm me-2"></i>
                <span>Este servicio <strong>no tiene fecha de impartición programada</strong>. Usa el botón "Programar Servicio" para completar la información.</span>
              </div>
            </Col>
          </Row>
        )}

        <Row className="printable-content">
          {/* Contenido principal */}
          <Col lg="8">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="8">
                    <h5 className="mb-0 text-white">
                      <i className="ni ni-box-2 me-2"></i>
                      Orden {order.order_number}
                    </h5>
                    <p className="text-sm text-white opacity-8 mb-0">
                      <i className="ni ni-calendar-grid-58 me-1"></i>
                      Creada el {parseDate(order.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </Col>
                  <Col md="4" className="text-end">
                    {editMode ? (
                      <Badge className="badge-lg bg-warning text-dark shadow-sm">
                        <i className="ni ni-settings me-1"></i>
                        Modo Edición
                      </Badge>
                    ) : order.quote ? (
                      <Badge className="badge-lg bg-white text-primary shadow-sm">
                        <i className="ni ni-check-bold me-1"></i>
                        Con propuesta
                      </Badge>
                    ) : (
                      <Badge className="badge-lg bg-white text-warning shadow-sm">
                        <i className="ni ni-fat-remove me-1"></i>
                        Sin Propuesta
                      </Badge>
                    )}
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="p-4">
                {/* 1. INFORMACIÓN DEL CLIENTE */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-building me-2"></i>
                    Información del Cliente
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Cliente:</strong>
                        <span className="text-muted ms-2">{consolidatedData.clientName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Razón Social:</strong>
                        <span className="text-muted ms-2">{consolidatedData.legalName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">RFC:</strong>
                        <Badge color="primary" className="badge-sm ms-2">{consolidatedData.rfc}</Badge>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Contacto:</strong>
                        <span className="text-muted ms-2">{consolidatedData.contactName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Email:</strong>
                        <span className="text-muted ms-2">{consolidatedData.contactEmail}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Teléfono:</strong>
                        <span className="text-muted ms-2">{consolidatedData.contactPhone}</span>
                      </p>
                    </Col>
                  </Row>
                  <p className="text-sm mb-0 mt-2">
                    <strong className="text-dark">
                      <i className="ni ni-pin-3 me-2"></i>Dirección Fiscal:
                    </strong>
                    <span className="text-muted ms-2">{consolidatedData.address}</span>
                  </p>
                </div>

                <hr className="horizontal dark" />

                {/* 2. VENDEDOR */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-badge me-2"></i>
                    Vendedor y Gestión
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Account Manager:</strong>
                        <Badge color="primary" className="ms-2">{consolidatedData.sellerName}</Badge>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">ID Proveedor:</strong>
                        <Badge color="primary" className="badge-sm ms-2">{consolidatedData.supplierId}</Badge>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* 3. DETALLES DEL SERVICIO */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-books me-2"></i>
                    Detalles del Servicio
                  </h6>
                  <Row>
                    <Col md="6">
                      {editMode ? (
                        <EditableText field="category" label="Categoría" value={editData.category} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Categoría:</strong>
                          <Badge color="primary" className="ms-2">{consolidatedData.category}</Badge>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="service_type" label="Tipo de Servicio" value={editData.service_type} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Tipo de Servicio:</strong>
                          <span className="text-muted ms-2">{consolidatedData.serviceType}</span>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="total_hours" label="Horas Totales" type="number" value={editData.total_hours} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Horas Totales:</strong>
                          <Badge color="primary" className="ms-2">{consolidatedData.totalHours} hrs</Badge>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="num_sessions" label="Sesiones" type="number" value={editData.num_sessions} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Sesiones:</strong>
                          <span className="text-muted ms-2">{consolidatedData.numSessions}</span>
                        </p>
                      )}
                    </Col>
                    <Col md="6">
                      {editMode ? (
                        <EditableText field="num_people" label="Personas" type="number" value={editData.num_people} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Personas:</strong>
                          <span className="text-muted ms-2">{consolidatedData.numPeople}</span>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="num_groups" label="Grupos" type="number" value={editData.num_groups} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Grupos:</strong>
                          <span className="text-muted ms-2">{consolidatedData.numGroups}</span>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="language" label="Idioma" value={editData.language} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Idioma:</strong>
                          <span className="text-muted ms-2">{consolidatedData.language}</span>
                        </p>
                      )}
                      {editMode ? (
                        <EditableText field="city_state" label="Ciudad" value={editData.city_state} onChange={handleChange} />
                      ) : (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Ciudad:</strong>
                          <span className="text-muted ms-2">{consolidatedData.cityState}</span>
                        </p>
                      )}
                    </Col>
                  </Row>
                  {consolidatedData.includes && (
                    <p className="text-sm mb-0 mt-2">
                      <strong className="text-dark">Incluye:</strong>
                      <span className="text-muted ms-2">{consolidatedData.includes}</span>
                    </p>
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* 4. LOGÍSTICA Y ENTREGA */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-delivery-fast me-2"></i>
                    Logística y Entrega
                    {!isScheduled && !editMode && (
                      <Badge color="warning" className="ms-2 text-dark">Sin programar</Badge>
                    )}
                  </h6>
                  {editMode ? (
                    <Row>
                      <Col md="6">
                        <EditableText field="service_delivery_date" label="Fecha de Impartición *" type="date" value={editData.service_delivery_date} onChange={handleChange} />
                        <EditableText field="scheduled_invoice_date" label="Fecha Programada de Facturación" type="date" value={editData.scheduled_invoice_date} onChange={handleChange} />
                        <EditableText field="location" label="Lugar de Impartición" value={editData.location} onChange={handleChange} />
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-2">
                          <Label className="text-xs font-weight-bold text-dark mb-1">Facilitador</Label>
                          <Input
                            type="select"
                            value={editData.facilitator || ''}
                            onChange={e => handleChange('facilitator', e.target.value)}
                            bsSize="sm"
                          >
                            <option value="">-- Sin asignar --</option>
                            {facilitatorsList.map(f => (
                              <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                          </Input>
                        </FormGroup>
                        <EditableText field="credit_days" label="Días de Crédito" type="number" value={editData.credit_days} onChange={handleChange} />
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col md="6">
                        {consolidatedData.serviceDeliveryDate && (
                          <p className="text-sm mb-2">
                            <strong className="text-dark">Fecha de Impartición:</strong>
                            <span className="text-muted ms-2">
                              {formatDateLocal(consolidatedData.serviceDeliveryDate)}
                            </span>
                          </p>
                        )}
                        {consolidatedData.orderDate && (
                          <p className="text-sm mb-2">
                            <strong className="text-dark">Fecha de Orden:</strong>
                            <span className="text-muted ms-2">
                              {formatDateLocal(consolidatedData.orderDate)}
                            </span>
                          </p>
                        )}
                        {consolidatedData.deliveryDate && (
                          <p className="text-sm mb-2">
                            <strong className="text-dark">Fecha de Entrega:</strong>
                            <span className="text-muted ms-2">
                              {formatDateLocal(consolidatedData.deliveryDate)}
                            </span>
                          </p>
                        )}
                      </Col>
                      <Col md="6">
                        {(order.facilitator_name || consolidatedData.facilitator) !== 'N/A' && (
                          <p className="text-sm mb-2">
                            <strong className="text-dark">
                              <i className="ni ni-single-02 me-1"></i>Facilitador:
                            </strong>
                            <Badge color="info" className="ms-2">
                              {order.facilitator_name || consolidatedData.facilitator}
                            </Badge>
                          </p>
                        )}
                        {(order.location || consolidatedData.location) && (
                          <p className="text-sm mb-2">
                            <strong className="text-dark">
                              <i className="ni ni-pin-3 me-1"></i>Lugar:
                            </strong>
                            <span className="text-muted ms-2">
                              {order.location || consolidatedData.location}
                            </span>
                          </p>
                        )}
                      </Col>
                    </Row>
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* 5. DETALLES DEL TALLER */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-hat-3 me-2"></i>
                    Detalles del Taller
                  </h6>
                  {editMode ? (
                    <Row>
                      <Col md="6">
                        <EditableText field="workshop_name" label="Nombre del Taller" value={editData.workshop_name} onChange={handleChange} />
                        <EditableText field="session_schedule" label="Horario de Sesión (ej. 09:00 - 13:00)" value={editData.session_schedule} onChange={handleChange} />
                        <div className="d-flex gap-3 mt-2">
                          <FormGroup check className="mb-2">
                            <Input
                              type="checkbox"
                              checked={editData.program_required || false}
                              onChange={e => handleChange('program_required', e.target.checked)}
                            />
                            <Label check className="text-sm">Programa requerido</Label>
                          </FormGroup>
                          <FormGroup check className="mb-2">
                            <Input
                              type="checkbox"
                              checked={editData.diplomas_required || false}
                              onChange={e => handleChange('diplomas_required', e.target.checked)}
                            />
                            <Label check className="text-sm">Diplomas requeridos</Label>
                          </FormGroup>
                        </div>
                      </Col>
                      <Col md="6">
                        <EditableText field="entry_contact_name" label="Contacto para ingreso (nombre)" value={editData.entry_contact_name} onChange={handleChange} />
                        <EditableText field="entry_contact_phone" label="Contacto para ingreso (teléfono)" value={editData.entry_contact_phone} onChange={handleChange} />
                      </Col>
                      <Col md="12">
                        <EditableText field="entry_requirements" label="Requisitos de Ingreso" textarea value={editData.entry_requirements} onChange={handleChange} />
                      </Col>
                    </Row>
                  ) : (
                    <>
                      {(invoiceSchedule?.workshop_name || invoiceSchedule?.session_schedule ||
                        invoiceSchedule?.entry_requirements || invoiceSchedule?.entry_contact_name) ? (
                        <Row>
                          <Col md="6">
                            {invoiceSchedule?.workshop_name && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">Nombre del Taller:</strong>
                                <span className="text-muted ms-2">{invoiceSchedule.workshop_name}</span>
                              </p>
                            )}
                            {invoiceSchedule?.session_schedule && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">
                                  <i className="ni ni-time-alarm me-1"></i>Horario:
                                </strong>
                                <Badge color="primary" className="ms-2">{invoiceSchedule.session_schedule}</Badge>
                              </p>
                            )}
                            {invoiceSchedule?.program_required !== undefined && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">Programa:</strong>
                                <Badge color={invoiceSchedule.program_required ? 'success' : 'primary'} className="ms-2">
                                  {invoiceSchedule.program_required ? 'Sí requerido' : 'No requerido'}
                                </Badge>
                              </p>
                            )}
                            {invoiceSchedule?.diplomas_required !== undefined && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">Diplomas:</strong>
                                <Badge color={invoiceSchedule.diplomas_required ? 'success' : 'primary'} className="ms-2">
                                  {invoiceSchedule.diplomas_required ? 'Sí requeridos' : 'No requeridos'}
                                </Badge>
                              </p>
                            )}
                          </Col>
                          <Col md="6">
                            {invoiceSchedule?.entry_contact_name && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">Contacto ingreso:</strong>
                                <span className="text-muted ms-2">{invoiceSchedule.entry_contact_name}</span>
                              </p>
                            )}
                            {invoiceSchedule?.entry_contact_phone && (
                              <p className="text-sm mb-2">
                                <strong className="text-dark">Tel. ingreso:</strong>
                                <span className="text-muted ms-2">{invoiceSchedule.entry_contact_phone}</span>
                              </p>
                            )}
                          </Col>
                          {invoiceSchedule?.entry_requirements && (
                            <Col md="12">
                              <div className="bg-gradient-light p-3 border-radius-lg shadow-sm mt-2">
                                <p className="text-xs text-uppercase text-muted mb-1">
                                  <i className="ni ni-check-bold me-1"></i>Requisitos de Ingreso
                                </p>
                                <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                                  {invoiceSchedule.entry_requirements}
                                </p>
                              </div>
                            </Col>
                          )}
                        </Row>
                      ) : (
                        <p className="text-sm text-muted">
                          <i className="ni ni-fat-add me-1"></i>
                          Sin detalles de taller. {' '}
                          <button className="btn btn-link btn-sm p-0" onClick={enterEditMode}>
                            Agregar información
                          </button>
                        </p>
                      )}
                    </>
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* 6. NECESIDAD DEL CLIENTE */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-bulb-61 me-2"></i>
                    Necesidad del Cliente
                  </h6>
                  {editMode ? (
                    <EditableText field="client_need" label="Descripción de la necesidad" textarea value={editData.client_need} onChange={handleChange} />
                  ) : (
                    (order.client_need || consolidatedData.clientNeed) ? (
                      <div className="bg-gradient-light p-4 border-radius-lg shadow-sm">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {order.client_need || consolidatedData.clientNeed}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted">
                        Sin descripción.{' '}
                        <button className="btn btn-link btn-sm p-0" onClick={enterEditMode}>
                          Agregar
                        </button>
                      </p>
                    )
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* 7. DESCRIPCIÓN DEL SERVICIO */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-align-left-2 me-2"></i>
                    Descripción del Servicio
                  </h6>
                  {editMode ? (
                    <EditableText field="description" label="Descripción (aparecerá en la factura)" textarea value={editData.description} onChange={handleChange} />
                  ) : (
                    consolidatedData.description && consolidatedData.description.trim() !== (order.client_need || consolidatedData.clientNeed || '').trim() ? (
                      <div className="bg-light p-3 border-radius-lg">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {consolidatedData.description}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted">
                        Sin descripción.{' '}
                        <button className="btn btn-link btn-sm p-0" onClick={enterEditMode}>
                          Agregar
                        </button>
                      </p>
                    )
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* 8. NOTAS */}
                <div className="mb-2">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-align-left-2 me-2"></i>
                    Notas Adicionales
                  </h6>
                  {editMode ? (
                    <EditableText field="notes" label="Notas" textarea value={editData.notes} onChange={handleChange} />
                  ) : (
                    (order.notes || invoiceSchedule?.notes) ? (
                      <>
                        {order.notes && (
                          <div className="bg-light p-3 border-radius-lg mb-2">
                            <p className="text-xs text-uppercase text-muted mb-1">Notas de la Orden</p>
                            <p className="text-sm mb-0 text-dark">{order.notes}</p>
                          </div>
                        )}
                        {invoiceSchedule?.notes && (
                          <div className="bg-light p-3 border-radius-lg">
                            <p className="text-xs text-uppercase text-muted mb-1">Notas de Facturación</p>
                            <p className="text-sm mb-0 text-dark">{invoiceSchedule.notes}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted">Sin notas.</p>
                    )
                  )}
                </div>

                {/* Botones de guardar al final en modo edición */}
                {editMode && (
                  <>
                    <hr className="horizontal dark" />
                    <div className="d-flex gap-2 justify-content-end">
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => setEditMode(false)}
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* SIDEBAR DERECHO */}
          <Col lg="4">
            {/* Archivos */}
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-folder-17 me-2"></i>Archivos
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                {order.pdf_file ? (
                  <>
                    <Button color="primary" size="sm" block className="mb-2 btn-icon shadow-sm"
                      onClick={() => openPdfViewer(order.pdf_file)}>
                      <span className="btn-inner--icon"><i className="ni ni-single-copy-04"></i></span>
                      <span className="btn-inner--text ms-2">Ver PDF Orden</span>
                    </Button>
                    <Button color="info" size="sm" block className="mb-2 btn-icon shadow-sm"
                      onClick={() => downloadPdf(order.pdf_file, `${order.order_number}.pdf`)}>
                      <span className="btn-inner--icon"><i className="ni ni-cloud-download-95"></i></span>
                      <span className="btn-inner--text ms-2">Descargar PDF</span>
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-3">
                    <i className="ni ni-folder-17 text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-xs text-muted mb-0 mt-2">No hay PDF disponible</p>
                  </div>
                )}
                {order.xml_file && (
                  <Button color="secondary" size="sm" block className="btn-icon shadow-sm mb-2"
                    onClick={() => downloadPdf(order.xml_file, `${order.order_number}.xml`)}>
                    <span className="btn-inner--icon"><i className="ni ni-archive-2"></i></span>
                    <span className="btn-inner--text ms-2">Descargar XML</span>
                  </Button>
                )}
                {quote?.pdf_file && (
                  <>
                    <hr className="horizontal dark my-2" />
                    <small className="text-uppercase text-xs font-weight-bold text-muted d-block mb-2">
                      Propuesta Original
                    </small>
                    <Button color="info" size="sm" block outline className="btn-icon shadow-sm"
                      onClick={() => openPdfViewer(quote.pdf_file)}>
                      <span className="btn-inner--icon"><i className="ni ni-single-copy-04"></i></span>
                      <span className="btn-inner--text ms-2">Ver PDF Propuesta</span>
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>

            {/* Facturación Programada */}
            {invoiceSchedule && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-calendar-grid-58 me-2"></i>Facturación Programada
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center border-radius-md">
                      <i className="ni ni-calendar-grid-58 text-white text-lg opacity-10"></i>
                    </div>
                    <div className="ms-3">
                      <p className="text-xs text-muted mb-0">Estado</p>
                      <h6 className="mb-0">
                        <Badge color={
                          invoiceSchedule.status === 'completed' ? 'success' :
                          invoiceSchedule.status === 'overdue' ? 'danger' :
                          invoiceSchedule.status === 'notified' ? 'warning' : 'info'
                        }>
                          {invoiceSchedule.status === 'completed' ? 'Completada' :
                           invoiceSchedule.status === 'overdue' ? 'Vencida' :
                           invoiceSchedule.status === 'notified' ? 'Notificada' : 'Pendiente'}
                        </Badge>
                      </h6>
                    </div>
                  </div>

                  <div className="bg-light p-2 border-radius-lg mb-3">
                    {invoiceSchedule.scheduled_invoice_date && (
                      <p className="text-xs mb-1">
                        <strong>Fecha Facturación:</strong>{' '}
                        {formatDateLocal(invoiceSchedule.scheduled_invoice_date)}
                      </p>
                    )}
                    {invoiceSchedule.service_delivery_date && (
                      <p className="text-xs mb-1">
                        <strong>Fecha Impartición:</strong>{' '}
                        {formatDateLocal(invoiceSchedule.service_delivery_date)}
                      </p>
                    )}
                    {invoiceSchedule.facilitator_name && (
                      <p className="text-xs mb-0">
                        <strong>Facilitador:</strong> {invoiceSchedule.facilitator_name}
                      </p>
                    )}
                  </div>

                  {invoiceSchedule.status !== 'completed' && invoiceSchedule.scheduled_invoice_date && (
                    <div className={`text-center p-2 border-radius-lg mb-3 ${
                      invoiceSchedule.is_overdue ? 'bg-gradient-danger' : 'bg-gradient-primary'
                    }`}>
                      <p className="text-xs text-white mb-0">
                        {invoiceSchedule.is_overdue ? (
                          <>
                            <i className="ni ni-time-alarm me-1"></i>
                            <strong>VENCIDA</strong> — {Math.abs(invoiceSchedule.days_until_invoice)} días de retraso
                          </>
                        ) : (
                          <>
                            <i className="ni ni-calendar-grid-58 me-1"></i>
                            Faltan <strong>{invoiceSchedule.days_until_invoice}</strong> días
                          </>
                        )}
                      </p>
                    </div>
                  )}

                  {!editMode && (
                    <Button color="warning" size="sm" block className="btn-icon" onClick={enterEditMode}>
                      <span className="btn-inner--icon"><i className="ni ni-settings"></i></span>
                      <span className="btn-inner--text ms-1">Editar Programación</span>
                    </Button>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Factura generada */}
            {order.has_invoice && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-money-coins me-2"></i>Factura Generada
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center border-radius-md">
                      <i className="ni ni-money-coins text-white text-lg opacity-10"></i>
                    </div>
                    <div className="ms-3">
                      <p className="text-xs text-muted mb-0">Estado</p>
                      <h6 className="mb-0 text-dark">
                        <Badge color="success">
                          <i className="ni ni-check-bold me-1"></i>Timbrada
                        </Badge>
                      </h6>
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="text-xs">Esta orden ya tiene factura generada y timbrada</span>
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <PDFViewerModal
        isOpen={pdfModalOpen}
        toggle={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdfUrl}
        title={`Orden ${order.order_number}`}
      />

      <style>{`
        @media print {
          .sidebar, .navbar, .btn, .min-height-300, .shadow { display: none !important; }
          .container-fluid { max-width: 100%; padding: 0; margin: 0; }
          .printable-content { display: block !important; width: 100% !important; }
          .col-lg-8, .col-lg-4 { width: 100% !important; max-width: 100% !important; }
          .card { border: 1px solid #dee2e6 !important; box-shadow: none !important; page-break-inside: avoid; margin-bottom: 1rem; }
          .card-body { padding: 1rem !important; }
          body { font-size: 11pt; color: #000; background: #fff; }
          h5, h6 { color: #000 !important; page-break-after: avoid; }
          .badge { display: inline-block !important; border: 1px solid #000; color: #000 !important; background: #fff !important; }
          .col-lg-4 .card { display: none; }
          .col-lg-8 { width: 100% !important; }
          @page { margin: 2cm; }
        }
      `}</style>
    </>
  );
}

export default FacilitationDetail;
